
import React, { useEffect } from 'react';
import { Target, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface GamePlanningTipsProps {
  className?: string;
}

// Storage key for tips visibility state
const TIPS_EXPANDED_KEY = 'game_planning_tips_expanded';

export const GamePlanningTips: React.FC<GamePlanningTipsProps> = ({ className = "" }) => {
  const isMobile = useIsMobile();
  
  // Initialize state from localStorage or default to expanded (true)
  const [isOpen, setIsOpen] = React.useState(() => {
    const savedState = localStorage.getItem(TIPS_EXPANDED_KEY);
    return savedState !== null ? savedState === 'true' : true;
  });

  // Save state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(TIPS_EXPANDED_KEY, isOpen.toString());
  }, [isOpen]);
  
  const tips = [
    'Position transitions that make sense',
    'Backup options for when your primary technique fails',
    'Both offensive and defensive scenarios',
    'Techniques you have already practiced regularly'
  ];

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className={`rounded-lg border border-border bg-card/50 p-4 shadow-sm ${className}`}
    >
      <div className="flex flex-col items-center">
        {/* Centered header section with icon */}
        <div className="text-center w-full flex items-center justify-center">
          <div className="flex items-center gap-3 mb-2">
            <div className={`${isMobile ? 'h-8 w-8' : 'h-12 w-12'} rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0`}>
              <Target className={`${isMobile ? 'h-4 w-4' : 'h-6 w-6'} text-primary`} />
            </div>
            <h3 className={`font-semibold ${isMobile ? 'text-lg' : 'text-xl'}`}>Game Planning Tips</h3>
          </div>
          
          {/* Collapsible trigger - now visible on both mobile and desktop */}
          <CollapsibleTrigger className="ml-4">
            {isOpen ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </CollapsibleTrigger>
        </div>
        
        <CollapsibleContent className="w-full">
          {/* Description centered on both mobile and desktop */}
          <p className="text-muted-foreground max-w-xl mb-5 mx-auto text-center">
            Build visual flow-chart style strategic game plans by connecting techniques into effective sequences for different scenarios.
          </p>
          
          {/* Bullet points in two columns and centered */}
          <div className="w-full max-w-3xl mx-auto">
            <div className={`grid ${isMobile ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-6'} w-full`}>
              {tips.map((tip, index) => (
                <div key={index} className="flex items-start gap-2 justify-center">
                  <CheckCircle2 className={`${isMobile ? 'h-4 w-4 mt-0' : 'h-5 w-5 mt-0.5'} text-primary flex-shrink-0`} />
                  <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground text-left`}>{tip}</span>
                </div>
              ))}
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};
